package ai.wagemix.pages

import ai.wagemix.analytics.WMCta
import ai.wagemix.analytics.WMCtaLocation
import ai.wagemix.analytics.sendWMCtaClicked
import ai.wagemix.components.layouts.sampleChartModel
import ai.wagemix.logic.WMRoutes
import ai.wagemix.siteStrings
import ai.wagemix.web.components.WMBandChart
import ai.wagemix.web.components.WMBandChartSize
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMChartCard
import ai.wagemix.web.components.WMChartSource
import ai.wagemix.web.components.WMFeatureCard
import ai.wagemix.web.components.WMFact
import ai.wagemix.web.components.WMFactCarousel
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMLead
import ai.wagemix.web.components.WMSectionHeading
import ai.wagemix.web.components.WMStepCard
import ai.wagemix.web.components.WMStory
import ai.wagemix.web.components.WMStoryCard
import ai.wagemix.web.theme.WMCardGridGapVar
import ai.wagemix.web.theme.WMDisplayTextStyle
import ai.wagemix.web.theme.WMHeroStackGapVar
import ai.wagemix.web.theme.WMSectionContentGapVar
import ai.wagemix.web.theme.WMSectionPaddingBlockVar
import ai.wagemix.web.theme.WMSectionTitleTextStyle
import androidx.compose.runtime.Composable
import com.fsryan.foundation.util.log.FSLog
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px

val HeroStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { repeat(1) { size(1.fr) } }.gap(WMHeroStackGapVar.value()) }
    FSBreakpoint.Large { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
}

val ThreeUpStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { repeat(1) { size(1.fr) } }.gap(WMCardGridGapVar.value()) }
    FSBreakpoint.Medium { Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } } }
}

val FourUpStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { repeat(1) { size(1.fr) } }.gap(WMCardGridGapVar.value()) }
    FSBreakpoint.Medium { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
    FSBreakpoint.Large { Modifier.gridTemplateColumns { repeat(4) { size(1.fr) } } }
}

/** Penpot `Landing`: static marketing content (exported and indexed); nothing here needs a presenter. */
@Page
@Layout(".components.layouts.MarketingLayout")
@Composable
fun HomePage(ctx: PageContext) {
    val s = siteStrings
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = Modifier.fillMaxWidth().gap(WMSectionPaddingBlockVar.value())) {
        // Hero
        Div(HeroStyle.toModifier().toAttrs()) {
            Column(modifier = Modifier.gap(WMSectionContentGapVar.value()), verticalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center) {
                SpanText("Know what a role is really worth.", WMDisplayTextStyle.toModifier())
                WMLead("Mix the occupations a job actually involves, weight them, and see the pay range the U.S. Bureau of Labor Statistics data supports, projected to this year.")
                Row(modifier = Modifier.gap(12.px).flexWrap(FlexWrap.Wrap)) {
                    WMButton("Start mixing — free", leadingIcon = WMIconGlyph.SlidersHorizontal) {
                        FSLog.sendWMCtaClicked(cta = WMCta.StartMixing, location = WMCtaLocation.Hero)
                        ctx.router.navigateTo(WMRoutes.MIX)
                    }
                    WMButton("See how it works", kind = WMButtonKind.Secondary) { ctx.router.navigateTo("#how-it-works") }
                }
            }
            WMChartCard(title = "Education Administrator + Graphic Designer", subtitle = "Sample mix · national · annual", footer = { WMChartSource(s.common.dataSource) }) {
                WMBandChart(model = sampleChartModel(), description = s.mixBuilder.chartDescription, size = WMBandChartSize.fixedHeight, eager = true)
            }
        }

        // Trust strip: one fact at a time in the centre, its neighbours half shown, to draw the eye.
        WMFactCarousel(
            facts = listOf(
                WMFact("100%", "U.S. Bureau of Labor Statistics data"),
                WMFact("800+", "occupations"),
                WMFact("P10–P90", "percentile bands, every year"),
                WMFact("24+ million", "data points"),
                WMFact("530", "states and metro areas"),
                WMFact("400+", "industries, from broad sectors to specific ones"),
                WMFact("5 years", "of wage history behind every projection"),
                WMFact("Every May", "updated with each BLS release"),
                WMFact("Annual & hourly", "every range, both ways")
            ),
            accessibilityString = "Wagemix at a glance",
            slideLabel = { position, count -> "$position of $count" }
        )

        // How it works
        Column(modifier = Modifier.fillMaxWidth().gap(WMSectionContentGapVar.value()).padding(top = 8.px).id("how-it-works")) {
            WMSectionHeading(overline = "How it works", title = "From job title to pay range in three steps")
            Div(ThreeUpStyle.toModifier().toAttrs()) {
                WMStepCard(1, "Pick the occupations", "Search 800+ BLS occupations, or upload a job description and let AI suggest the mix.")
                WMStepCard(2, "Weight the mix", "Drag the sliders to match how the role really spends its time. Lock a weight to keep it fixed.")
                WMStepCard(3, "See the whole range", "Get the median, middle 50% and typical range by year, with projections and area filters.")
            }
        }

        // Features
        Column(modifier = Modifier.fillMaxWidth().gap(WMSectionContentGapVar.value())) {
            WMSectionHeading(
                overline = "Features",
                title = "Everything you need to price a real role",
                lead = "Job titles rarely map to one occupation. Wagemix is built for the roles in between."
            )
            Div(FourUpStyle.toModifier().toAttrs()) {
                WMFeatureCard(WMIconGlyph.Sparkles, "AI mix from a job description", "Upload a PDF or paste a posting URL from Lever, Workday, iCIMS and more. AI maps it to weighted occupations.")
                WMFeatureCard(WMIconGlyph.ChartLine, "The full pay range", "See P10 to P90 bands by year, not a single average. Projected years are clearly marked.")
                WMFeatureCard(WMIconGlyph.MapPin, "Area and industry filters", "Narrow the data to a state, metro area or industry to match where the role is based.")
                WMFeatureCard(WMIconGlyph.Award, "Share with a pay badge", "Share a link to your mix. Add a pay range and earn a Bronze to Diamond badge for job posts.")
            }
        }

        // Stories
        Column(modifier = Modifier.fillMaxWidth().gap(WMSectionContentGapVar.value())) {
            WMSectionHeading(
                overline = "Stories",
                title = "How people use Wagemix",
                lead = "HR teams use it to price postings. Candidates use it to check offers."
            )
            Div(ThreeUpStyle.toModifier().toAttrs()) {
                WMStoryCard(WMStory(
                    quote = "The offer was for a role that's half administrator, half designer. Wagemix put it below the BLS median for that mix, so I countered with real data instead of a hunch.",
                    occupations = listOf("Education Administrator", "Graphic Designer"),
                    initials = "SM", name = "Sarah M.", role = "Preschool Director"
                ))
                WMStoryCard(WMStory(
                    quote = "Our next hire covers development, marketing and project management. I priced the posting from the job description, and the range holds up because it's straight from BLS data.",
                    occupations = listOf("Software Developer", "Marketing Manager", "Project Manager"),
                    initials = "MT", name = "Marcus T.", role = "Head of People, SaaS startup"
                ))
                WMStoryCard(WMStory(
                    quote = "Moving from nursing into analysis, I uploaded each job description I was offered to see what that job should pay. One offer came in under the 25th percentile, and I walked away.",
                    occupations = listOf("Registered Nurse", "Management Analyst"),
                    initials = "JL", name = "Jennifer L.", role = "Job seeker"
                ))
            }
        }

        // Pricing teaser + CTA band
        Column(
            modifier = Modifier.fillMaxWidth().padding(40.px).gap(16.px).borderRadius(24.px)
                .backgroundColor(colors.primaryContainer).color(colors.onPrimaryContainer),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText("Start free. Upgrade when you need adjustable weights, filters and AI mixes.", WMSectionTitleTextStyle.toModifier().textAlign(TextAlign.Center).maxWidth(720.px))
            Row(modifier = Modifier.gap(12.px).flexWrap(FlexWrap.Wrap)) {
                WMButton("Start mixing — free") {
                    FSLog.sendWMCtaClicked(cta = WMCta.StartMixing, location = WMCtaLocation.CtaBand)
                    ctx.router.navigateTo(WMRoutes.MIX)
                }
                WMButton(s.common.viewPlans, kind = WMButtonKind.Secondary) {
                    FSLog.sendWMCtaClicked(cta = WMCta.ViewPlans, location = WMCtaLocation.CtaBand)
                    ctx.router.navigateTo(WMRoutes.PRICING)
                }
            }
        }
    }
}
